import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase";
import { withRetry } from "../utils/supabaseErrorHandler";
import "./TripProfitability.css";
import "./TripScheduler.css";

const formatPrice = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(3);
};

const parseNumber = (value) => {
  const num = Number(String(value ?? "").replace(",", "."));
  return Number.isNaN(num) ? 0 : num;
};

const formatMoney = (value) =>
  parseNumber(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const formatDateFr = (isoDate) => {
  if (!isoDate) return "—";
  return new Date(isoDate).toLocaleDateString("fr-FR");
};

const monthStartIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
};

const todayIso = () => new Date().toISOString().split("T")[0];

const computeTripTotals = (productPrices) => {
  const lines = Array.isArray(productPrices) ? productPrices : [];
  return lines.reduce(
    (acc, item) => {
      const qty = parseNumber(item.quantity);
      const sell = parseNumber(item.price);
      const buy = parseNumber(item.prix_usine);
      const revenue = qty * sell;
      const cost = qty * buy;
      return {
        revenue: acc.revenue + revenue,
        cost: acc.cost + cost,
        margin: acc.margin + (revenue - cost),
      };
    },
    { revenue: 0, cost: 0, margin: 0 }
  );
};

const emptyEditForm = () => ({
  id: "",
  truckId: "",
  destination: "",
  date: "",
  cargo: "",
  description: "",
  status: "completed",
  color: "#1E3A8A",
});

const TripHistory = () => {
  const [trucks, setTrucks] = useState([]);
  const [trips, setTrips] = useState([]);
  const [scheduleProducts, setScheduleProducts] = useState([]);
  const [scheduleDestinations, setScheduleDestinations] = useState([]);
  const [schedulePrices, setSchedulePrices] = useState([]);
  const [truckId, setTruckId] = useState("");
  const [dateFrom, setDateFrom] = useState(monthStartIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm());
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showAllProducts, setShowAllProducts] = useState(false);

  const fetchCatalog = useCallback(async () => {
    const [productsRes, destinationsRes, pricesRes] = await Promise.all([
      supabase.from("schedule_products").select("*").order("name", { ascending: true }),
      supabase.from("schedule_destinations").select("*").order("name", { ascending: true }),
      supabase.from("schedule_destination_prices").select("*"),
    ]);
    if (!productsRes.error) setScheduleProducts(productsRes.data || []);
    if (!destinationsRes.error) setScheduleDestinations(destinationsRes.data || []);
    if (!pricesRes.error) setSchedulePrices(pricesRes.data || []);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trucksRes, tripsRes] = await Promise.all([
        withRetry(() =>
          supabase.from("trucks").select("id, immatriculation").order("immatriculation", { ascending: true })
        ),
        withRetry(() =>
          supabase
            .from("trips")
            .select("*")
            .eq("status", "completed")
            .gte("date", dateFrom)
            .lte("date", dateTo)
            .order("date", { ascending: false })
        ),
      ]);
      if (trucksRes.error) throw trucksRes.error;
      if (tripsRes.error) throw tripsRes.error;

      setTrucks(trucksRes.data || []);
      setTrips(tripsRes.data || []);
      await fetchCatalog();
    } catch (err) {
      console.error("Error loading trip history:", err);
      setError("Échec du chargement de l'historique.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, fetchCatalog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const truckMap = useMemo(() => {
    const map = {};
    trucks.forEach((t) => {
      map[t.id] = t.immatriculation || t.id;
    });
    return map;
  }, [trucks]);

  const filteredTrips = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return trips.filter((trip) => {
      if (truckId && trip.truck_id !== truckId) return false;
      if (!q) return true;
      const plate = (truckMap[trip.truck_id] || "").toLowerCase();
      return (
        (trip.destination || "").toLowerCase().includes(q) ||
        (trip.cargo || "").toLowerCase().includes(q) ||
        (trip.description || "").toLowerCase().includes(q) ||
        plate.includes(q)
      );
    });
  }, [trips, truckId, searchQuery, truckMap]);

  const getProductsForDestination = (destinationName) => {
    if (!destinationName) return [];
    const destination = scheduleDestinations.find(
      (d) => d.name.toLowerCase() === destinationName.toLowerCase()
    );
    if (!destination) return [];
    return schedulePrices
      .filter((price) => price.destination_id === destination.id)
      .map((price) => {
        const product = scheduleProducts.find((p) => p.id === price.product_id);
        if (!product) return null;
        return {
          productId: product.id,
          productName: product.name,
          price: Number(price.price),
          prixUsine: Number(product.prix_usine ?? 0),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.productName.localeCompare(b.productName, "fr"));
  };

  const buildProductPricesPayload = (products) =>
    products.map((item) => ({
      product_id: item.productId,
      product_name: item.productName,
      price: parseNumber(item.price),
      prix_usine: parseNumber(item.prixUsine ?? 0),
      quantity: parseNumber(item.quantity ?? 0),
    }));

  const openEdit = (trip) => {
    setEditForm({
      id: trip.id,
      truckId: trip.truck_id || "",
      destination: trip.destination || "",
      date: trip.date,
      cargo: trip.cargo || "",
      description: trip.description || "",
      status: trip.status || "completed",
      color: trip.color || "#1E3A8A",
    });
    setSelectedProducts(
      (Array.isArray(trip.product_prices) ? trip.product_prices : []).map((item) => {
        const catalog = scheduleProducts.find((p) => p.id === item.product_id);
        return {
          productId: item.product_id,
          productName: item.product_name,
          price: formatPrice(item.price),
          prixUsine: formatPrice(
            item.prix_usine != null ? item.prix_usine : (catalog?.prix_usine ?? 0)
          ),
          quantity: item.quantity != null && item.quantity !== "" ? String(item.quantity) : "",
        };
      })
    );
    setShowAllProducts(false);
    setError(null);
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditForm(emptyEditForm());
    setSelectedProducts([]);
    setShowAllProducts(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    if (name === "destination") {
      setSelectedProducts([]);
      setShowAllProducts(false);
    }
  };

  const toggleProduct = (item) => {
    setSelectedProducts((prev) => {
      const exists = prev.some((p) => p.productId === item.productId);
      if (exists) return prev.filter((p) => p.productId !== item.productId);
      return [
        ...prev,
        {
          productId: item.productId,
          productName: item.productName,
          price: formatPrice(item.price),
          prixUsine: formatPrice(item.prixUsine ?? 0),
          quantity: "",
        },
      ];
    });
  };

  const updateProductField = (productId, field, value) => {
    setSelectedProducts((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, [field]: value } : p))
    );
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!editForm.destination || !editForm.date || !editForm.cargo) {
      setError("Destination, date et cargaison sont obligatoires.");
      return;
    }
    if (selectedProducts.some((p) => parseNumber(p.quantity) <= 0)) {
      setError("Indiquez une quantité supérieure à 0 pour chaque produit sélectionné.");
      return;
    }
    setSaving(true);
    try {
      const productPrices = buildProductPricesPayload(selectedProducts);
      const { error: updateError } = await withRetry(() =>
        supabase
          .from("trips")
          .update({
            truck_id: editForm.truckId || null,
            destination: editForm.destination,
            date: editForm.date,
            cargo: editForm.cargo,
            description: editForm.description,
            product_prices: productPrices,
            status: editForm.status,
            color: editForm.color,
          })
          .eq("id", editForm.id)
      );
      if (updateError) throw updateError;

      if (editForm.status !== "completed") {
        setTrips((prev) => prev.filter((t) => t.id !== editForm.id));
      } else {
        setTrips((prev) =>
          prev.map((t) =>
            t.id === editForm.id
              ? {
                  ...t,
                  truck_id: editForm.truckId || null,
                  destination: editForm.destination,
                  date: editForm.date,
                  cargo: editForm.cargo,
                  description: editForm.description,
                  product_prices: productPrices,
                  status: editForm.status,
                  color: editForm.color,
                }
              : t
          )
        );
      }
      closeEdit();
      setSuccess("Voyage modifié avec succès.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la modification du voyage.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trip) => {
    const label = `${formatDateFr(trip.date)} — ${trip.destination}`;
    if (
      !window.confirm(
        `Supprimer définitivement ce voyage ?\n${label}\n\nCette action est irréversible.`
      )
    ) {
      return;
    }
    setError(null);
    try {
      const { error: deleteError } = await withRetry(() =>
        supabase.from("trips").delete().eq("id", trip.id)
      );
      if (deleteError) throw deleteError;
      setTrips((prev) => prev.filter((t) => t.id !== trip.id));
      if (showEditModal && editForm.id === trip.id) closeEdit();
      setSuccess("Voyage supprimé.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error(err);
      setError("Erreur lors de la suppression du voyage.");
    }
  };

  const renderProductPicker = () => {
    const available = getProductsForDestination(editForm.destination);
    if (!editForm.destination) {
      return <p className="trip-pricing-empty">Choisissez une destination.</p>;
    }

    const selectedMap = Object.fromEntries(
      selectedProducts.map((item) => [item.productId, item])
    );
    const rows = showAllProducts
      ? available
      : selectedProducts.map((sel) => {
          const catalog = available.find((a) => a.productId === sel.productId);
          return {
            productId: sel.productId,
            productName: sel.productName,
            price: catalog ? catalog.price : Number(sel.price),
            prixUsine: Number(sel.prixUsine ?? catalog?.prixUsine ?? 0),
          };
        });

    if (!showAllProducts && selectedProducts.length === 0) {
      return (
        <div className="trip-product-picker">
          <p className="trip-pricing-empty">Aucun produit sélectionné.</p>
          <button
            type="button"
            className="trip-product-expand-btn"
            onClick={() => setShowAllProducts(true)}
          >
            Ajouter / modifier des produits
          </button>
        </div>
      );
    }

    return (
      <div className="trip-product-picker">
        {rows.map((item) => {
          const selected = selectedMap[item.productId];
          const checked = Boolean(selected);
          return (
            <div
              key={item.productId}
              className={`trip-product-option ${checked ? "selected" : ""}`}
            >
              <label className="trip-product-option-check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleProduct(item)}
                />
                <span className="trip-product-option-name">{item.productName}</span>
              </label>
              {checked ? (
                <div className="trip-product-option-fields">
                  <label className="trip-product-field">
                    <span>Qté</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="trip-product-qty-input"
                      value={selected.quantity ?? ""}
                      onChange={(e) =>
                        updateProductField(item.productId, "quantity", e.target.value)
                      }
                      placeholder=""
                    />
                  </label>
                  <label className="trip-product-field">
                    <span>Prix vente</span>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      className="trip-product-price-input"
                      value={selected.price}
                      onChange={(e) =>
                        updateProductField(item.productId, "price", e.target.value)
                      }
                    />
                  </label>
                </div>
              ) : (
                <span className="trip-product-option-price">{formatPrice(item.price)}</span>
              )}
            </div>
          );
        })}
        {!showAllProducts ? (
          <button
            type="button"
            className="trip-product-expand-btn"
            onClick={() => setShowAllProducts(true)}
          >
            Ajouter / modifier des produits
          </button>
        ) : (
          <button
            type="button"
            className="trip-product-expand-btn"
            onClick={() => setShowAllProducts(false)}
          >
            Masquer les autres produits
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="trip-profit-container">
      <aside className="sidebar">
        <h2 className="trip-profit-fleet-title">Système de Gestion & Contrôle</h2>
        <nav>
          <ul>
            <li><Link to="/fleet/dashboard">📊 Gestion de Flotte</Link></li>
            <li><Link to="/cash-tracking">💵 Gestion de Caisse</Link></li>
            <li><Link to="/parc">🔧 Gestion des Pièces</Link></li>
            <li><Link to="/fleet/stock-carburant">⛽ Stock Carburant</Link></li>
            <li><Link to="/stock">📦 Gestion de Stock</Link></li>
            <li><Link to="/schedule">🗓️ Gestion des Programmes</Link></li>
            <li className="active"><Link to="/schedule/history">📜 Historique voyages</Link></li>
            <li><Link to="/schedule/profitability">📈 Rentabilité voyages</Link></li>
            <li><Link to="/maintenance">🛠️ Maintenance</Link></li>
            <li><Link to="/trailers">🚛 Gestion des Remorques</Link></li>
            <li><Link to="/incidents">🚨 Gestion des Incidents</Link></li>
            <li><Link to="/driver-payments">💰 Gestion des salaires </Link></li>
            <li><Link to="/chatbot">🤖 Système de Reporting</Link></li>
          </ul>
        </nav>
        <div className="trip-profit-sidebar-footer">
          <p>Version 1.2.0</p>
          <p>© 2025</p>
        </div>
      </aside>

      <main className="trip-profit-content">
        <header className="trip-profit-header">
          <div>
            <h1>📜 Historique des voyages</h1>
            <p className="trip-profit-subtitle">
              Voyages terminés. Corrigez une erreur avec Modifier, ou Supprimer définitivement.
            </p>
          </div>
          <div className="trip-history-header-actions">
            <Link to="/schedule/profitability" className="trip-profit-back-btn">
              📈 Rentabilité
            </Link>
            <Link to="/schedule" className="trip-profit-back-btn">
              ← Programmes
            </Link>
          </div>
        </header>

        <section className="trip-profit-filters">
          <div className="trip-profit-filter-group">
            <label htmlFor="historyTruck">Camion</label>
            <select
              id="historyTruck"
              value={truckId}
              onChange={(e) => setTruckId(e.target.value)}
            >
              <option value="">Tous les camions</option>
              {trucks.map((truck) => (
                <option key={truck.id} value={truck.id}>
                  {truck.immatriculation}
                </option>
              ))}
            </select>
          </div>
          <div className="trip-profit-filter-group">
            <label htmlFor="historyFrom">Du</label>
            <input
              id="historyFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="trip-profit-filter-group">
            <label htmlFor="historyTo">Au</label>
            <input
              id="historyTo"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          <div className="trip-profit-filter-group trip-history-search-group">
            <label htmlFor="historySearch">Recherche</label>
            <input
              id="historySearch"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Destination, cargaison, camion..."
            />
          </div>
          <button type="button" className="trip-profit-refresh-btn" onClick={fetchData}>
            Actualiser
          </button>
        </section>

        {success && <div className="trip-scheduler-success-message">{success}</div>}
        {error && !showEditModal && <div className="trip-profit-error">{error}</div>}
        {loading && <p>Chargement...</p>}

        {!loading && filteredTrips.length === 0 && (
          <p className="trip-profit-empty">
            Aucun voyage terminé sur cette période.
            Utilisez « Terminer » dans les programmes pour les archiver ici.
          </p>
        )}

        {!loading && filteredTrips.length > 0 && (
          <section className="trip-profit-table-wrap">
            <table className="trip-profit-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Camion</th>
                  <th>Destination</th>
                  <th>Cargaison</th>
                  <th>Produits</th>
                  <th>Marge</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTrips.map((trip) => {
                  const totals = computeTripTotals(trip.product_prices);
                  return (
                    <tr key={trip.id}>
                      <td>{formatDateFr(trip.date)}</td>
                      <td>{truckMap[trip.truck_id] || "Non assigné"}</td>
                      <td>{trip.destination}</td>
                      <td>{trip.cargo}</td>
                      <td>
                        {(trip.product_prices || []).length === 0
                          ? "—"
                          : (trip.product_prices || [])
                              .map((p) => `${p.product_name}×${parseNumber(p.quantity)}`)
                              .join(", ")}
                      </td>
                      <td className={totals.margin >= 0 ? "positive" : "negative"}>
                        {formatMoney(totals.margin)}
                      </td>
                      <td>
                        <div className="trip-history-actions">
                          <button
                            type="button"
                            className="trip-profit-expand-btn"
                            onClick={() => openEdit(trip)}
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            className="trip-history-delete-btn"
                            onClick={() => handleDelete(trip)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {showEditModal && (
          <div className="modal-overlay">
            <div className="trip-scheduler-modal-content trip-history-modal">
              <button className="trip-scheduler-modal-close" type="button" onClick={closeEdit}>
                ✕
              </button>
              <section className="trip-scheduler-form-section">
                <h2>Modifier le voyage (historique)</h2>
                {error && <div className="trip-scheduler-error-message">{error}</div>}
                <form onSubmit={handleSaveEdit} className="trip-scheduler-form">
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="historyEditTruck">Camion</label>
                    <select
                      id="historyEditTruck"
                      name="truckId"
                      value={editForm.truckId}
                      onChange={handleEditChange}
                    >
                      <option value="">-- Aucun camion --</option>
                      {trucks.map((truck) => (
                        <option key={truck.id} value={truck.id}>
                          {truck.immatriculation}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="historyEditDestination">
                      Destination <span className="required-field">*</span>
                    </label>
                    <select
                      id="historyEditDestination"
                      name="destination"
                      value={editForm.destination}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="">-- Choisir --</option>
                      {scheduleDestinations.map((destination) => (
                        <option key={destination.id} value={destination.name}>
                          {destination.name}
                        </option>
                      ))}
                      {editForm.destination &&
                        !scheduleDestinations.some(
                          (d) => d.name.toLowerCase() === editForm.destination.toLowerCase()
                        ) && (
                          <option value={editForm.destination}>
                            {editForm.destination} (ancienne)
                          </option>
                        )}
                    </select>
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label>Produits / Tarifs</label>
                    {renderProductPicker()}
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="historyEditDate">
                      Date <span className="required-field">*</span>
                    </label>
                    <input
                      id="historyEditDate"
                      type="date"
                      name="date"
                      value={editForm.date}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="historyEditCargo">
                      Cargaison <span className="required-field">*</span>
                    </label>
                    <input
                      id="historyEditCargo"
                      type="text"
                      name="cargo"
                      value={editForm.cargo}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="historyEditDescription">Description</label>
                    <textarea
                      id="historyEditDescription"
                      name="description"
                      value={editForm.description}
                      onChange={handleEditChange}
                      rows="3"
                    />
                  </div>
                  <div className="trip-scheduler-form-group">
                    <label htmlFor="historyEditStatus">Statut</label>
                    <select
                      id="historyEditStatus"
                      name="status"
                      value={editForm.status}
                      onChange={handleEditChange}
                    >
                      <option value="completed">Terminé</option>
                      <option value="scheduled">Planifié (remettre au planning)</option>
                      <option value="in_progress">En cours</option>
                    </select>
                  </div>
                  <div className="trip-scheduler-form-actions">
                    <button type="submit" className="trip-scheduler-save-btn" disabled={saving}>
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      className="trip-scheduler-cancel-btn"
                      onClick={closeEdit}
                      disabled={saving}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="trip-history-delete-btn"
                      onClick={() =>
                        handleDelete({
                          id: editForm.id,
                          date: editForm.date,
                          destination: editForm.destination,
                        })
                      }
                      disabled={saving}
                    >
                      Supprimer
                    </button>
                  </div>
                </form>
              </section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TripHistory;
